import { api } from '@goaltree/backend/convex/_generated/api'
import { Checkbox } from '@goaltree/ui/components/checkbox'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { profileUrl } from '@goaltree/ui/lib/social-share'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { Download, Loader2, Lock, Share2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CATEGORY_COLORS } from '@/lib/category-color'

export type ShareBoardItem = {
  id: string
  title: string
  category: string
  imageUrl: string | null
  isPublic: boolean
  done: boolean
}

type Format = 'square' | 'story'
const SIZES: Record<Format, { w: number; h: number }> = {
  square: { w: 1080, h: 1080 },
  story: { w: 1080, h: 1920 },
}
const MAX = 6
const FONT = 'Inter, system-ui, -apple-system, sans-serif'

// Fetched as blobs (Convex storage answers CORS) so the canvas stays untainted
// and toBlob works; anything that fails falls back to a category gradient.
const bitmaps = new Map<string, Promise<ImageBitmap | null>>()
function loadBitmap(url: string): Promise<ImageBitmap | null> {
  let p = bitmaps.get(url)
  if (!p) {
    p = fetch(url)
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((b) => createImageBitmap(b))
      .catch(() => null)
    bitmaps.set(url, p)
  }
  return p
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number, lines: number): string[] {
  const words = text.split(/\s+/)
  const out: string[] = []
  let line = ''
  for (const w of words) {
    const next = line ? `${line} ${w}` : w
    if (ctx.measureText(next).width <= maxW || !line) line = next
    else {
      out.push(line)
      line = w
    }
  }
  if (line) out.push(line)
  if (out.length <= lines) return out
  const kept = out.slice(0, lines)
  let last = kept[lines - 1]
  while (last && ctx.measureText(`${last}…`).width > maxW) last = last.slice(0, -1)
  kept[lines - 1] = `${last.trimEnd()}…`
  return kept
}

async function renderCollage(
  format: Format,
  items: ShareBoardItem[],
  heading: string,
  link: string | null,
): Promise<Blob | null> {
  const { w, h } = SIZES[format]
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  const bmps = await Promise.all(items.map((i) => (i.imageUrl ? loadBitmap(i.imageUrl) : null)))

  ctx.fillStyle = '#0b0a0b'
  ctx.fillRect(0, 0, w, h)
  const pad = 64
  const top = format === 'story' ? 220 : 170
  const bottom = format === 'story' ? 160 : 110
  ctx.fillStyle = '#ffffff'
  ctx.font = `800 ${format === 'story' ? 64 : 52}px ${FONT}`
  ctx.textBaseline = 'alphabetic'
  wrap(ctx, heading, w - pad * 2, 1).forEach((l) => ctx.fillText(l, pad, top - 50))

  const n = items.length
  const cols = format === 'story' ? (n <= 1 ? 1 : 2) : n <= 1 ? 1 : n <= 4 ? 2 : 3
  const rows = Math.max(1, Math.ceil(n / cols))
  const gap = 18
  const cellW = (w - pad * 2 - gap * (cols - 1)) / cols
  const cellH = Math.min(cellW * 1.25, (h - top - bottom - gap * (rows - 1)) / rows)
  const gridH = rows * cellH + gap * (rows - 1)
  const y0 = top + (h - top - bottom - gridH) / 2

  items.forEach((item, i) => {
    const x = pad + (i % cols) * (cellW + gap)
    const y = y0 + Math.floor(i / cols) * (cellH + gap)
    ctx.save()
    ctx.beginPath()
    ctx.roundRect(x, y, cellW, cellH, 28)
    ctx.clip()
    const bmp = bmps[i]
    if (bmp) {
      const s = Math.max(cellW / bmp.width, cellH / bmp.height)
      const dw = bmp.width * s
      const dh = bmp.height * s
      ctx.drawImage(bmp, x + (cellW - dw) / 2, y + (cellH - dh) / 2, dw, dh)
    } else {
      const c = CATEGORY_COLORS[item.category.toLowerCase()] ?? '#6b7280'
      const g = ctx.createLinearGradient(x, y, x + cellW, y + cellH)
      g.addColorStop(0, '#141214')
      g.addColorStop(1, c)
      ctx.fillStyle = g
      ctx.fillRect(x, y, cellW, cellH)
    }
    const shade = ctx.createLinearGradient(0, y + cellH * 0.45, 0, y + cellH)
    shade.addColorStop(0, 'rgba(0,0,0,0)')
    shade.addColorStop(1, 'rgba(0,0,0,0.82)')
    ctx.fillStyle = shade
    ctx.fillRect(x, y, cellW, cellH)
    const size = cols === 3 ? 30 : 38
    ctx.font = `800 ${size}px ${FONT}`
    ctx.fillStyle = '#ffffff'
    const lines = wrap(ctx, item.done ? `${item.title} ✓` : item.title, cellW - 48, 2)
    lines.forEach((l, li) =>
      ctx.fillText(l, x + 24, y + cellH - 26 - (lines.length - 1 - li) * size * 1.15),
    )
    ctx.restore()
  })

  ctx.font = `600 ${format === 'story' ? 36 : 30}px ${FONT}`
  ctx.fillStyle = 'rgba(255,255,255,0.65)'
  ctx.fillText(link ?? 'goaltree.ai', pad, h - bottom / 2 + 10)
  ctx.textAlign = 'right'
  ctx.fillStyle = '#ff7aa0'
  ctx.fillText('Goaltree', w - pad, h - bottom / 2 + 10)

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
}

export function ShareBoardDialog({
  open,
  onOpenChange,
  items,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  items: ShareBoardItem[]
}) {
  const { t } = useTranslation()
  const user = useQuery(api.auth.getCurrentUser)
  const handle = useQuery(api.preferences.getMyHandle)
  const [format, setFormat] = useState<Format>('square')
  const [selected, setSelected] = useState<string[]>([])
  const [blob, setBlob] = useState<Blob | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [rendering, setRendering] = useState(false)
  const seeded = useRef(false)

  useEffect(() => {
    if (!open) {
      seeded.current = false
      return
    }
    if (seeded.current || items.length === 0) return
    seeded.current = true
    const pub = items.filter((i) => i.isPublic)
    setSelected(
      [...pub.filter((i) => i.imageUrl), ...pub.filter((i) => !i.imageUrl)]
        .slice(0, MAX)
        .map((i) => i.id),
    )
  }, [open, items])

  const name = (user?.name as string | undefined)?.split(' ')[0]?.trim()
  const heading = name
    ? t('goalsBoard.shareHeading', { name, year: new Date().getFullYear() })
    : t('goalsBoard.shareHeadingAnon', { year: new Date().getFullYear() })
  const link = handle ? profileUrl(handle).replace(/^https?:\/\//, '') : null
  const chosen = selected.flatMap((id) => items.find((i) => i.id === id) ?? [])
  const key = `${format}|${heading}|${link}|${chosen.map((c) => `${c.id}:${c.imageUrl}`).join(',')}`

  useEffect(() => {
    if (!open || chosen.length === 0) {
      setBlob(null)
      return
    }
    let live = true
    setRendering(true)
    void renderCollage(format, chosen, heading, link).then((b) => {
      if (!live) return
      setBlob(b)
      setRendering(false)
    })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key])

  useEffect(() => {
    if (!blob) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(blob)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [blob])

  const file = blob ? new File([blob], 'goaltree-vision.png', { type: 'image/png' }) : null
  const canShareFile =
    !!file && typeof navigator !== 'undefined' && !!navigator.canShare?.({ files: [file] })

  function toggle(id: string, on: boolean) {
    setSelected((prev) => {
      if (!on) return prev.filter((x) => x !== id)
      if (prev.length >= MAX) {
        toast(t('goalsBoard.shareMax', { count: MAX }))
        return prev
      }
      return [...prev, id]
    })
  }

  function download() {
    if (!preview) return
    const a = document.createElement('a')
    a.href = preview
    a.download = 'goaltree-vision.png'
    a.click()
  }

  async function share() {
    if (!file) return
    try {
      await navigator.share({ files: [file], text: link ? `https://${link}` : undefined })
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') toast.error(t('common.error'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92svh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-3xl">
        <DialogTitle>{t('goalsBoard.shareBoard')}</DialogTitle>
        <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(0,16rem)]">
          <div className="flex flex-col items-center gap-3">
            <div className="flex rounded-full border border-border p-0.5 text-xs font-semibold">
              {(['square', 'story'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormat(f)}
                  className={cn(
                    'cursor-pointer rounded-full px-3 py-1 transition-colors',
                    format === f
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(f === 'square' ? 'goalsBoard.formatSquare' : 'goalsBoard.formatStory')}
                </button>
              ))}
            </div>
            <div
              className={cn(
                'relative flex w-full items-center justify-center overflow-hidden rounded-xl border border-border bg-[#0b0a0b]',
                format === 'square' ? 'aspect-square max-w-sm' : 'aspect-[9/16] max-w-[15rem]',
              )}
            >
              {preview ? (
                <img src={preview} alt="" className="h-full w-full object-contain" />
              ) : (
                <p className="px-6 text-center text-xs text-white/60">
                  {chosen.length === 0 ? t('goalsBoard.shareNone') : ''}
                </p>
              )}
              {rendering && <Loader2 className="absolute h-6 w-6 animate-spin text-white/70" />}
            </div>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <p className="text-xs text-muted-foreground">
              {t('goalsBoard.sharePick', { count: MAX })}
            </p>
            <div className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
              {items.map((i) => {
                const on = selected.includes(i.id)
                return (
                  <label
                    key={i.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-muted/50"
                  >
                    <Checkbox checked={on} onCheckedChange={(v) => toggle(i.id, v === true)} />
                    <span className="min-w-0 flex-1 truncate">{i.title}</span>
                    {!i.isPublic && (
                      <span className="flex shrink-0 items-center gap-1 text-[10.5px] text-muted-foreground">
                        <Lock className="h-3 w-3" />
                        {t('goalsBoard.private')}
                      </span>
                    )}
                  </label>
                )
              })}
            </div>
            <div className="mt-auto flex gap-2 pt-2">
              <button
                type="button"
                onClick={download}
                disabled={!preview || rendering}
                className="flex h-9 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full bg-primary text-sm font-bold text-primary-foreground transition-opacity disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {t('goalsBoard.download')}
              </button>
              {canShareFile && (
                <button
                  type="button"
                  onClick={share}
                  disabled={rendering}
                  className="flex h-9 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full border border-border text-sm font-bold transition-colors hover:bg-muted disabled:opacity-50"
                >
                  <Share2 className="h-4 w-4" />
                  {t('goalsBoard.shareAction')}
                </button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
